import { z } from 'zod';
import { INTAKE_CLASSES, LINK_CONTEXTS, VISIBILITY_VALUES } from '../constants.js';
import { NestCreateSchema } from './nest.js';
// ─── Bundle schema v2 (CTSK-102 P1) ────────────────────────────────────
//
// Extends the existing bundle envelope (containers / tags / rules /
// fileWorkflows / nest) with two new top-level sections: `projects` +
// `workItems`. Design ratified 2026-07-26 on CTSK-102: COO requirements
// draft (comment `01KY85Z3GP8H0FJ1VZPT0DTG9V`) + dr.the0's operator
// sign-off with the mandatory-`bundleUid` amendment (comment
// `01KYFTD4MVWC04C0JMZK9V97V5`). See CTSK-102 for the full R1-R8 + AC
// list.
//
// This file is the SCHEMA scaffolding (P1). P2 wires the importer to
// consume + upsert on `bundleUid`. P3 adds --dry-run + reverse export.
// P4 ships docs + the first example seed (External Clients Nest self-
// seeding via new sections).
//
// FIELD-NAMING NOTE — camelCase, not snake_case
// ---------------------------------------------
// The COO requirements draft + dr.the0's sign-off used snake_case for
// new field shorthand (`bundle_uid`, `bundle_schema_version`,
// `project_ref`, `due_date_offset`, etc.). The physical schema below
// normalizes to camelCase for consistency with the existing bundle
// envelope (`bundleVersion`, `fileWorkflows`) and the rest of
// `packages/shared/src/schemas/`. The concept-to-field mapping:
//
//   bundle_uid              → bundleUid
//   bundle_schema_version   → bundleSchemaVersion
//   default_workflow_handle → defaultWorkflowHandle
//   project_ref             → projectRef
//   parent_ref              → parentRef
//   due_date_offset         → dueDateOffset
//   source_url              → sourceUrl (already the shared-schema name)
//
// Bundle authors reading the ratified spec should translate accordingly.
/** The bundle envelope's schema-version integer. Absent → v1 (legacy
 *  4-section shape; every shipped bundle before 2026-07-26). Present +
 *  === 2 → v2 (this schema; may include `projects[]` + `workItems[]`).
 *  Present + unknown value → REJECT (importer's P2 job; the schema
 *  parses it as literal 2 so an unknown version fails validation
 *  loudly rather than silently downgrading). */
export const BUNDLE_SCHEMA_VERSION_2 = 2;
/** v3 (CTSK-340) — adds `comments[]`, `documents[]`, `documentLinks[]`.
 *  Design ratified 2026-08-15 on CTSK-340 (design comment
 *  `01M03ASH3M4J4PZVK1949GN9V9` + operator sign-off on Q1/Q2/Q3).
 *
 *  NOTE for bundle authors: the version gate is `bundleSchemaVersion`
 *  (this integer), NOT `bundleVersion` — the latter is a free-form
 *  semver string naming the app build that produced the bundle. */
export const BUNDLE_SCHEMA_VERSION_3 = 3;
/** Sentinel string an operator may place in a seed's `owner` field to
 *  request runtime resolution via `--owner-user-id <ULID>` importer
 *  flag. Keeps ULIDs out of committed bundles (identity leakage +
 *  cross-org non-portability). Resolution + hard-error-on-unresolved
 *  live in P2. */
export const PLACEHOLDER_OWNER = '$$OWNER_USER_ID$$';
/** Cap on `bundleUid` length. Long enough for `nest-slug-project`
 *  compound names; short enough that a typo is visually catchable. */
export const BUNDLE_UID_MAX_LENGTH = 128;
/** Cap on any bundle-scoped string (project.name, workItem.title,
 *  etc.). Matches the existing shared-schema limits for these fields
 *  (see ProjectCreateSchema.name.max, WorkItemCreateSchema.title.max
 *  in `project.ts` + `work-item.ts`). */
export const BUNDLE_STRING_MAX_LENGTH = 200;
/** Cap on description-shaped fields inside a bundle. */
export const BUNDLE_DESCRIPTION_MAX_LENGTH = 2000;
// ─── Bundle-uid references ─────────────────────────────────────────────
//
// A reference from one seed to another (workItem.projectRef,
// workItem.parentRef, workItem.dependencies[]) can take two shapes:
//
//   {"$ref": "some-uid"}  — resolves to a sibling seed in this bundle
//                           by matching its `bundleUid`. This is the
//                           preferred shape for fresh-install bundles
//                           where the target doesn't exist in the DB
//                           yet.
//
//   "CTSK"                — bare string; resolves to a pre-existing
//                           row's canonical key. This shape enables
//                           cross-bundle references (e.g. workItems in
//                           one bundle depending on a project seeded
//                           by another).
//
// Resolution precedence lives in P2's importer: try bundleUid within
// bundle first, then fall back to canonical key. This schema just
// carries the shape; it never resolves.
export const BundleUidRefSchema = z.union([
    z.object({ $ref: z.string().min(1).max(BUNDLE_UID_MAX_LENGTH) }).strict(),
    z.string().min(1).max(BUNDLE_UID_MAX_LENGTH),
]);
export function classifyBundleRef(ref) {
    if (typeof ref === 'string' && ref.length > 0)
        return 'externalKey';
    if (typeof ref === 'object' &&
        ref !== null &&
        '$ref' in ref &&
        typeof ref.$ref === 'string' &&
        (ref.$ref).length > 0) {
        return 'bundleUid';
    }
    return 'invalid';
}
// ─── Project seed ──────────────────────────────────────────────────────
//
// Shape mirrors ProjectCreateSchema (in `project.ts`) with the bundle-
// specific additions (`bundleUid`, `defaultWorkflowHandle` for handle-
// not-ULID resolution, `owner` for the placeholder-sentinel dance).
// `.strict()` catches typos like `bundleUuid` or `default_workflow_id`
// at parse time instead of silently dropping them.
export const BundleProjectSeedSchema = z
    .object({
    /** REQUIRED bundle-scoped stable id. Also the upsert key on
     *  re-import (kills the brittle `(project_key, title)` upsert in
     *  R6). Format: bundle authors choose; recommend
     *  `<nest-slug>-project` or `<domain>-<purpose>`. Must be unique
     *  within the bundle. */
    bundleUid: z.string().min(1).max(BUNDLE_UID_MAX_LENGTH),
    /** Canonical project key — the CTSK-style short handle a user
     *  types to reference the project. Must match ProjectCreateSchema
     *  regex (uppercase-first, letters/digits only). Collision-checked
     *  on import (P2). */
    key: z
        .string()
        .min(2)
        .max(16)
        .regex(/^[A-Z][A-Z0-9]+$/, 'uppercase letters/digits, must start with letter'),
    name: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH),
    description: z.string().max(BUNDLE_DESCRIPTION_MAX_LENGTH).nullable().optional(),
    /** Workflow reference by HANDLE (not ULID) — P2's importer
     *  resolves to the org's shipped workflow. Absent → importer uses
     *  the org's default workflow. Unknown handle → P2 hard-errors
     *  listing available handles (per dr.the0's Q3 amendment). */
    defaultWorkflowHandle: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH).optional(),
    visibility: z.enum(VISIBILITY_VALUES).optional(),
    /** Owner may be a placeholder sentinel (`$$OWNER_USER_ID$$`) OR a
     *  real ULID. P2's importer resolves sentinels via
     *  `--owner-user-id <ULID>` flag; unresolved sentinels hard-error
     *  at import time (per dr.the0's Q2 amendment). P1 stores whatever
     *  string arrives. */
    owner: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH).optional(),
    sourceUrl: z
        .union([
        z.literal('').transform(() => null),
        z.string().trim().max(2048).url(),
        z.null(),
    ])
        .optional(),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
})
    .strict();
// ─── Work-item seed ────────────────────────────────────────────────────
/** Statuses valid as an import target. Mirrors the workflow engine's
 *  known unstarted-stage statuses; P2 may accept more if the resolved
 *  workflow defines them, but seed-time targets should stick to the
 *  common set. */
const IMPORT_TARGET_STATUSES = ['backlog', 'todo', 'ready'];
/** Priority levels — mirrors the shared WorkItemCreateSchema. */
const WORK_ITEM_PRIORITIES = ['critical', 'high', 'medium', 'low'];
/** Type values — mirrors the shared WorkItemCreateSchema. */
const WORK_ITEM_TYPES = ['task', 'issue', 'epic', 'feature', 'adr', 'troubleshooting'];
export const BundleWorkItemSeedSchema = z
    .object({
    /** REQUIRED bundle-scoped stable id. Same rules as
     *  BundleProjectSeed.bundleUid. Referenced by other seeds'
     *  `parentRef` / `dependencies[]`. */
    bundleUid: z.string().min(1).max(BUNDLE_UID_MAX_LENGTH),
    /** Reference to the seed's parent project. `{"$ref": "<uid>"}`
     *  form resolves within the bundle's `projects[]`; bare string
     *  form matches an existing project's canonical key
     *  (cross-bundle). See BundleUidRefSchema. REQUIRED — a work
     *  item without a project has no valid workflow. */
    projectRef: BundleUidRefSchema,
    type: z.enum(WORK_ITEM_TYPES),
    title: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH),
    description: z.string().max(BUNDLE_DESCRIPTION_MAX_LENGTH).nullable().optional(),
    status: z.enum(IMPORT_TARGET_STATUSES),
    priority: z.enum(WORK_ITEM_PRIORITIES),
    labels: z.array(z.string().min(1).max(64)).optional(),
    /** Bundle-uid references to sibling work-item seeds this depends
     *  on, or bare canonical keys for cross-bundle. P2 resolves. */
    dependencies: z.array(BundleUidRefSchema).optional(),
    /** Optional parent-child link. Same ref shape as `projectRef`. */
    parentRef: BundleUidRefSchema.nullable().optional(),
    /** Integer days from import time — P2 computes the absolute
     *  `dueDate` at insert. Avoids stale absolute dates in committed
     *  bundles. Negative values allowed (already-overdue on import). */
    dueDateOffset: z.number().int().optional(),
    /** Placeholder sentinels or real ULIDs. Same resolution rules as
     *  BundleProjectSeed.owner. P1 stores verbatim. */
    assignees: z.array(z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH)).optional(),
})
    .strict();
// ─── Comment seed (v3 — CTSK-340) ──────────────────────────────────────
//
// Per-workitem comment. The seed's `bundleUid` is written verbatim to
// `work_item_comments.external_id` at import — that column already
// exists for exactly this purpose ("External id for importer re-runs.
// Scoped per work item") and carries a partial unique index
// `WHERE external_id IS NOT NULL`, so re-importing the same bundle
// updates rather than duplicates. No new idempotency plumbing.
//
// ORDERING (design decision D4): array order is NOT load-bearing.
// `createdAtOffset` is the sort key and the importer re-derives the
// absolute timestamp at install time. Same rationale as
// `BundleWorkItemSeed.dueDateOffset` — committed bundles must not
// carry absolute dates that rot. Unit differs on purpose: days is far
// too coarse for a comment thread, so this one is MINUTES.
export const BundleCommentSeedSchema = z
    .object({
    /** REQUIRED bundle-scoped stable id. Lands in
     *  `work_item_comments.external_id`; must be unique within the
     *  bundle's `comments[]`. */
    bundleUid: z.string().min(1).max(64),
    /** The work item this comment hangs off. `{"$ref": "<uid>"}` form
     *  resolves within the bundle's `workItems[]`; bare-string form
     *  matches an existing item's canonical key (e.g. `CTSK-42`). */
    workItemRef: BundleUidRefSchema,
    body: z.string().min(1).max(BUNDLE_DESCRIPTION_MAX_LENGTH),
    /** Placeholder sentinel (`$$OWNER_USER_ID$$`) or a real ULID —
     *  identical resolution rules to `BundleProjectSeed.owner`, handled
     *  by the same `--owner-user-id` importer flag. Absent → resolves
     *  to the importing user. */
    authorRef: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH).optional(),
    /** Integer MINUTES from import time. Negative → authored in the
     *  past (the normal case for a seeded thread). Absent → 0. */
    createdAtOffset: z.number().int().optional(),
})
    .strict();
// ─── Document seed (v3 — CTSK-340) ─────────────────────────────────────
//
// A STANDALONE document, not an attachment. It may be linked to zero or
// many work items via `documentLinks[]` — which is exactly why its
// container is declared here rather than inherited from a linked item
// (design decision D2: inheritance is undefined whenever the link count
// isn't exactly 1).
//
// SCOPE (design decision Q3, operator-ratified): inline markdown only.
// The `documents` table is storage-backed (sourcePath / storagePath /
// contentHash / sidecarPath + a queued fileTask), so the importer
// routes `body` through the normal ingest path as a
// `structured_note`. Binary/uploaded documents are out of scope for v3.
export const BundleDocumentSeedSchema = z
    .object({
    /** REQUIRED bundle-scoped stable id. Referenced by
     *  `documentLinks[].documentRef`. Also seeds the deterministic
     *  `doc_<yyyymmdd>_<slug>` row id at import. */
    bundleUid: z.string().min(1).max(BUNDLE_UID_MAX_LENGTH),
    title: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH),
    /** Inline markdown body. Written to storage by the importer. */
    body: z.string().min(1),
    /** Intake classification. Absent → `structured_note`. */
    docType: z.enum(INTAKE_CLASSES).optional(),
    tags: z.array(z.string().min(1).max(64)).optional(),
    /** Storage container by NAME, resolved against this bundle's own
     *  `containers[]` first and then the org's existing containers —
     *  the same name-based precedence the `rules` section already uses.
     *  Absent → the org default container. */
    containerRef: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH).optional(),
    /** Placeholder sentinel or real ULID; same rules as
     *  `BundleProjectSeed.owner`. */
    owner: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH).optional(),
    visibility: z.enum(VISIBILITY_VALUES).optional(),
})
    .strict();
// ─── Document↔work-item link seed (v3 — CTSK-340) ──────────────────────
//
// Deliberately has NO `bundleUid`: `document_work_item_links` is keyed
// `(document_id, work_item_id, context)`, so the triple IS the identity
// and re-import is idempotent for free (design decision D3).
//
// `context` reuses the shipped `LINK_CONTEXTS` vocabulary verbatim —
// source / reference / output / evidence / requirement / attachment. No
// new vocabulary, no migration (design decision D5).
export const BundleDocumentLinkSeedSchema = z
    .object({
    /** `{"$ref": "<uid>"}` into this bundle's `workItems[]`, or a bare
     *  canonical key for a pre-existing item. */
    workItemRef: BundleUidRefSchema,
    /** `{"$ref": "<uid>"}` into this bundle's `documents[]`, or a bare
     *  existing document id. */
    documentRef: BundleUidRefSchema,
    context: z.enum(LINK_CONTEXTS),
    note: z.string().max(BUNDLE_DESCRIPTION_MAX_LENGTH).nullable().optional(),
})
    .strict();
// ─── Bundle envelope (v2) ──────────────────────────────────────────────
//
// Top-level structure. NOT `.strict()` on purpose — future sections
// (workflows, milestones, etc.) may be added without a bundle re-mint.
// Per-section schemas (BundleProjectSeedSchema,
// BundleWorkItemSeedSchema) ARE `.strict()` for the typo-guard.
//
// The existing sub-schemas (containers, tags, rules, fileWorkflows) are
// intentionally NOT re-validated here — the current importer applies
// per-section validation on its own path. Extending this envelope's
// coverage to those sections is future work (P2+ or a separate
// hygiene ticket).
/** Cap on `nest.landingPath` length. Mirrors CTSK-347's
 *  `tool.landingPath` cap (tool.ts). */
export const NEST_LANDING_PATH_MAX_LENGTH = 512;
/** CTSK-384 — the bundle's top-level `nest:` block, now a first-class
 *  shared schema (previously an inline mirror in
 *  apps/api/src/routes/admin.ts, ctsk-055). Extends the CRUD-side
 *  NestCreateSchema with the bundle-only `landingPath` field:
 *
 *  Where clicking the nest's sidebar/Mission Control tile lands.
 *  Mirrors CTSK-347's `tool.manifest.landingPath` at the nest layer.
 *  Three accepted shapes (spec merge of the CTSK-384 ticket — `/` or
 *  absolute, CTSK-347-identical — and the operative brief — `?`-leading
 *  query string like `?tab=pending`):
 *    - `?query=string` → composed onto the nest's existing base path
 *      by the web resolver (`nestTileDocsPath`)
 *    - `/relative/route` → wins outright, same-tab navigation
 *    - absolute http(s) URL → wins outright
 *  Unset → existing behavior (native page; ECN keeps its CTSK-357
 *  hardcoded `?tab=pending` fallback). Persisted to
 *  `nests.landing_path` by the bundle importer (migration 0054);
 *  bundle-driven only for v1 — no post-install edit UI (non-goal). */
// .strip() restores Zod's default unknown-key tolerance for bundle
// blocks — CTSK-445 made NestCreateSchema strict, and .extend() would
// otherwise inherit `unknownKeys: "strict"`. Bundle-import operator
// behavior deliberately unchanged (CTSK-445 non-goal).
export const BundleNestBlockSchema = NestCreateSchema.extend({
    landingPath: z.string().min(1).max(NEST_LANDING_PATH_MAX_LENGTH).refine((v) => v.startsWith('?') || v.startsWith('/') || /^https?:\/\//.test(v), { message: 'landingPath must be a query string (starting ?), a relative route (starting /), or an absolute URL (http[s]://)' }).optional(),
}).strip();
export const BundleV2Schema = z.object({
    $schema: z.literal('captain-tasko-bundle').optional(),
    bundleVersion: z.string().optional(),
    /** Absent → v1 (backward compat). Present → must equal 2 or 3;
     *  anything else fails Zod parse loudly (per dr.the0's Q6 amendment
     *  "rejects unknown future versions"). Widened from `literal(2)` by
     *  CTSK-340; 4+ still hard-fails. */
    bundleSchemaVersion: z
        .union([z.literal(BUNDLE_SCHEMA_VERSION_2), z.literal(BUNDLE_SCHEMA_VERSION_3)])
        .optional(),
    /** CTSK-303/285 — OPTIONAL top-level bundle identity. `bundleUid` is
     *  a stable id for the bundle AS A WHOLE (distinct from the per-seed
     *  bundleUid fields inside projects[]/workItems[]); `name` is the
     *  operator-facing label. Both feed (a) the DSR `installed_by:
     *  bundle:<uid>` provenance stamp and (b) the Settings → Bundles
     *  installed-history card. Absent on every pre-0.1.122 bundle —
     *  fully backward compatible. */
    bundleUid: z.string().min(1).max(BUNDLE_UID_MAX_LENGTH).optional(),
    name: z.string().min(1).max(BUNDLE_STRING_MAX_LENGTH).optional(),
    nest: z.unknown().optional(),
    containers: z.array(z.unknown()).optional(),
    tags: z.array(z.unknown()).optional(),
    rules: z.array(z.unknown()).optional(),
    fileWorkflows: z.array(z.unknown()).optional(),
    /** NEW in v2. Optional — a bundle may still ship only the legacy
     *  4 sections. */
    projects: z.array(BundleProjectSeedSchema).optional(),
    /** NEW in v2. Optional. */
    workItems: z.array(BundleWorkItemSeedSchema).optional(),
    /** CTSK-303 — tool REFERENCES (operator-ratified 2026-08-06).
     *  Bundles do not carry tool manifests — tools install only via
     *  POST /tools (CTSK-292). A `tools[]` entry references an
     *  ALREADY-INSTALLED tool by slug; at import, each resolved
     *  reference has its registry files (re-)registered as
     *  `internal-registry` entries in the org's Data Sources Registry
     *  with `installed_by: bundle:<uid>`. Unknown slugs surface in
     *  `summary.tools.errors` without failing the import. */
    tools: z.array(z.object({
        slug: z.string().min(1).max(120),
    }).strict()).optional(),
    /** NEW in v3 (CTSK-340). Defaults to `[]` rather than `undefined` so
     *  every downstream consumer can iterate unconditionally — a v1 or v2
     *  bundle parsed through this schema yields three empty arrays, which
     *  is behaviourally identical to the pre-v3 shape. */
    comments: z.array(BundleCommentSeedSchema).default([]),
    /** NEW in v3 (CTSK-340). See BundleCommentSeedSchema note on default. */
    documents: z.array(BundleDocumentSeedSchema).default([]),
    /** NEW in v3 (CTSK-340). See BundleCommentSeedSchema note on default. */
    documentLinks: z.array(BundleDocumentLinkSeedSchema).default([]),
});
/** Alias — the envelope schema now covers v2 AND v3. `BundleV2Schema`
 *  keeps its name so the ~40 existing import sites don't churn; new
 *  code should prefer this alias for clarity. */
export const BundleEnvelopeSchema = BundleV2Schema;
/** True when the bundle carries content that only v3 can express. The
 *  exporter uses this to stamp the LOWEST version that expresses the
 *  payload (design decision D6.5) — a bundle with three empty v3
 *  sections still ships as v2, keeping the footprint of existing
 *  bundles unchanged. */
export function hasV3Content(bundle) {
    return ((bundle.comments?.length ?? 0) > 0 ||
        (bundle.documents?.length ?? 0) > 0 ||
        (bundle.documentLinks?.length ?? 0) > 0);
}
/** One `tools[]` reference in a bundle (CTSK-303). */
export const BundleToolRefSchema = z.object({
    slug: z.string().min(1).max(120),
}).strict();
// ─── Installed-bundle audit events (CTSK-285) ──────────────────────────
//
// Over-the-wire shape for GET /admin/bundles/installed — one event per
// applied (non-dry-run) bundle import, sourced from the
// `admin.bundle_import` audit rows. Rows written before 0.1.122 carry
// counts only (no item names / bundle identity / warnings) — those
// shape to `legacy: true` and the UI renders a "details unavailable"
// note instead of the section item lists.
/** Sections an import event may report. Superset of the importer's
 *  summary keys (the `tools` key is the CTSK-303 reference section). */
export const BUNDLE_INSTALL_SECTION_KEYS = [
    'nest',
    'containers',
    'tags',
    'rules',
    'fileWorkflows',
    'workflows',
    'customProperties',
    'projects',
    'workItems',
    'tools',
    // CTSK-340 (v3)
    'comments',
    'documents',
    'documentLinks',
];
export const BundleInstallSectionSchema = z.object({
    inserted: z.number().int().nonnegative(),
    updated: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
    errors: z.array(z.string()),
    /** Names of the items this section processed (tag names, rule names,
     *  CP keys, tool slugs, project keys, work-item titles). Null on
     *  legacy (pre-0.1.122) events. Capped server-side at
     *  BUNDLE_INSTALL_NAMES_CAP with `namesTruncated`. */
    names: z.array(z.string()).nullable(),
    namesTruncated: z.boolean().optional(),
});
/** Cap on per-section item-name lists persisted to the audit row +
 *  echoed by the endpoint (work-item-heavy bundles balloon). */
export const BUNDLE_INSTALL_NAMES_CAP = 100;
export const BundleInstallEventSchema = z.object({
    /** Audit-row ULID — stable key for the UI list. */
    id: z.string(),
    /** Operator-facing label: top-level bundle `name` ?? nest.name ?? null. */
    bundleName: z.string().nullable(),
    /** Top-level bundleUid when the bundle declared one; null otherwise
     *  (every pre-0.1.122 bundle). */
    bundleUid: z.string().nullable(),
    bundleSchemaVersion: z.number().int().nullable(),
    nest: z.object({
        slug: z.string(),
        code: z.string(),
        name: z.string(),
    }).nullable(),
    installedAt: z.string(),
    installedBy: z.object({
        id: z.string(),
        name: z.string().nullable(),
        email: z.string().nullable(),
    }).nullable(),
    sections: z.record(BundleInstallSectionSchema),
    warnings: z.array(z.string()),
    /** Always false today — dry-runs are not audited. Kept for
     *  forward-compat should preview auditing ever land. */
    dryRun: z.boolean(),
    /** True for pre-0.1.122 audit rows: counts are real, but item names,
     *  bundle identity and warnings were not captured at import time. */
    legacy: z.boolean(),
});
/** Each section's uid namespace is INDEPENDENT — a project and a work
 *  item may share a uid without conflict, and CTSK-340's `comments` /
 *  `documents` follow the same rule. `documentLinks` is absent on
 *  purpose: it carries no bundleUid (its identity is the
 *  document/workItem/context triple). */
export function findDuplicateBundleUids(bundle) {
    const issues = [];
    const check = (section, items) => {
        if (!items)
            return;
        const byUid = new Map();
        items.forEach((item, i) => {
            const existing = byUid.get(item.bundleUid);
            if (existing)
                existing.push(i);
            else
                byUid.set(item.bundleUid, [i]);
        });
        for (const [bundleUid, atIndexes] of byUid.entries()) {
            if (atIndexes.length > 1)
                issues.push({ section, bundleUid, atIndexes });
        }
    };
    check('projects', bundle.projects);
    check('workItems', bundle.workItems);
    check('comments', bundle.comments);
    check('documents', bundle.documents);
    return issues;
}
//# sourceMappingURL=bundle.js.map